import React from 'react';
import FilterFormAbstract from "../../Abstract/Filter/FilterFormAbstract";
import { Container, FieldWrap, FilterCombo, Position, PuzzleButton, PuzzleNumericField } from "puzzle";
import { Field } from "../../UI/Field/Field";
import {
  HBox,
  PuzzleColorPicker,
  PuzzleInput,
  PuzzlePanel,
  PuzzleRadio,
  PuzzleTagField,
  VBox,
  withFormControl
} from "puzzle";
import { ColorPickerChangeEvent, RadioButtonChangeEvent } from "@progress/kendo-react-inputs";
import {
  FILTER,
  FILTER_DATA,
  FILTER_FORM_PROPS,
  FILTER_FORM_STATE,
} from "../../Dashboard/Filters/Types";
import {
  commonLocalizationGetter,
  getColorPalette,
  getFavoritesFilterCount,
  getFormattedDate,
  pingForFavoritesFilterCount,
  PALETTE
} from "../../util/Util";
import LocalizedDatePicker from "../../util/LocalizedDatePicker/LocalizedDatePicker";
import { config } from "../../Dashboard/Filters/FilterForm.config";
import * as _ from 'lodash';
import { arrayMove, SortableContainer, SortableElement } from "react-sortable-hoc";
import AdvancedSearchListShowHideItem from "./AdvancedSearchListShowHideItem";
import RemoteTagField from "../../Dashboard/Filters/RemoteTagField/RemoteTagField";
import CustomFieldsFilter from "../../Dashboard/Filters/CustomFields/CustomFieldsFilter";
import { ComboBoxChangeEvent, MultiSelectChangeEvent } from "@progress/kendo-react-dropdowns";

type ADVANCED_SEARCH_PROPS = FILTER_FORM_PROPS
  & {
  filterFormData: FILTER_DATA,
  filters: FILTER[],
  customAttributesValues: []
}
type ADVANCED_SEARCH_STATE = FILTER_FORM_STATE
  & {
  customFieldsActive: boolean,
  filterFormData: FILTER_DATA,
  mode: string
}

const SortableItem = SortableElement(({ value, showHideSearch, deleteSearch, editedFilterId, onSearchClick }: any) =>
  <AdvancedSearchListShowHideItem
    currentItem={value}
    hasDropdown={false}
    showHideSearch={showHideSearch}
    deleteSearch={deleteSearch}
    editedFilterId={editedFilterId}
    onSearchClick={onSearchClick}
  />
);

const SortableList = SortableContainer((props: any) => {
  const { items } = props;
  return (
    <ul
      className='saved-filters-group'
      aria-label={props.ariaLabel}
    >
      {items.length ?
        <>
          {!!items.length && items.map((value: any, index: any) => (
            <SortableItem
              key={`item-${value.filterId}`}
              index={index}
              value={value}
              showHideSearch={props.showHideSearch}
              deleteSearch={props.deleteSearch}
              editedFilterId={props.editedFilterId}
              onSearchClick={props.onSearchClick}
            />
          ))}
        </> :
        <VBox
          alignItemsCenter
          justifyContentCenter
          gap={4}
          className={'tp-filter-list-empty'}
        >
          <span className={'no-saved-searches'} />
          <span className={'tp-color-light-LM tp-filter-list-empty-text'}>{props.emptyFilterListLabel}</span>
        </VBox>
      }
    </ul>
  );
});

export class AdvancedSearchDialog extends FilterFormAbstract<ADVANCED_SEARCH_PROPS, ADVANCED_SEARCH_STATE> {
  colorPalette: PALETTE = getColorPalette();
  colorPaletteForPicker: PALETTE = _.invert(this.colorPalette);
  favoritesFilterCountIds: number[] = [];
  favoriteFilterUrl: string = '/jobPhase/filters/count';
  favoritesFilterCountProperty: string = 'jobPhaseCount';
  /**
   * Prevent filter update state when component is unmounted
   */
  isComponentMounted: boolean = false;

  state: ADVANCED_SEARCH_STATE = {
    ...this.state,
    mode: 'create'
  }
  lastSelectedSearch: null | FILTER = null;

  taskStatusData = ['AVAILABLE', 'CANCELLED', 'COMPLETED', 'IN_PROGRESS', 'READY', 'RESERVED', 'RETURNED'].map((item: string) => {
    return {
      name: commonLocalizationGetter(`TASK.statuses.${item}`),
      value: item
    }
  });

  moduleDidMount = () => {
    this.isComponentMounted = true;
    const { modeData, filterData, savedSearches } = this.props;
    let values: any = {
      color: this.defaultColor
    };
    if (['clone', 'edit'].includes(modeData.mode)) {
      values = this.decodeEditData(modeData);
    }
    const { filterName, mode } = modeData;
    if (filterName) {
      this.props.form.setData({ filterName: mode === 'clone' ? filterName + ' CLONE' : filterName });
    }
    if (mode === 'edit' || mode === 'clone') {
      this.lastSelectedSearch = { ...values };
    }
    let { projectValues } = values;
    if (projectValues?.length) {
      let params: { projectIds: string[] } = {
        projectIds: projectValues
      };
      this.dispatch('clearSearchFormAndGetProjectRelatedData', Position.end, { params });
    }

    this.favoritesFilterCountIds = _.filter(savedSearches, (filterItem: any) => !filterItem.submissionCount).map((filterItem: any) => filterItem.filterId);
    let customFieldsFilters = this.getAllCustomFieldsFilters(filterData);
    let customFieldsActive = customFieldsFilters && !!customFieldsFilters.length;
    this.setState({
        customFieldsActive,
        filterData,
        filters: savedSearches,
        mode: modeData.mode,
        projectRadio: projectValues?.length ? 'selectedProject' : 'allProjects',
        values
      }, () => {

        this.dispatch('getCountAndLoadCustomFields', Position.end, {
          customFieldsActive,
          disabledClearAll: this.updateClearButton(),
          taskCountData: _.omit(this.lastSelectedSearch, 'filterId'),
          createFilterForm: true,
          fireMainGridFilter: false,
          values
        });
      }
    )
  };

  /**
   * Component will unmount
   * - stop pending ping for submission count
   */
  componentWillUnmount(): void {
    this.isComponentMounted = false;
    this.dispatch('stopPingingRequest', Position.end);
    super.componentWillUnmount();
  }

  /**
   * If data are unchanged allow changes. Display warning dialog otherwise
   *
   * @param currentItem
   */
  checkForSearchChanges = (currentItem: FILTER) => {
    let { customFieldsActive, values } = this.state;
    if (customFieldsActive) {
      values.customControlValues = [];
    }
    values.filterName = this.props.form.getData().filterName;

    let phasesName = this.getPropertyFromFilterData('PHASES_REMOTE_COMBO', 'name');
    if (phasesName) {
      values = this.parseRemoteComboData(phasesName, values);
    }
    let dataChanged = !_.isEmpty(this.editFilterInitialData) && !_.isEqual(_.omit(values, ['jobPhaseCount']), _.omit(this.editFilterInitialData, ['jobPhaseCount']));
    if (dataChanged && this.lastSelectedSearch?.filterId) {
      this.dispatch('savedSearchWillBeChangedWarning', '', {
        nextItem: currentItem
      })
    } else {
      (this.lastSelectedSearch as any) = currentItem;
      if (_.isEmpty(currentItem)) {
        this.clearAllSearchData();
      } else {
        this.updateFormWithSelectedSearch(currentItem)
      }
      this.setState({ mode: _.isEmpty(currentItem) ? 'create' : 'edit' });
    }

    if (customFieldsActive) {
      this.dispatch('customFieldsFilterReset', Position.end);
    }
  };

  /**
   * Clears all data from form
   */
  clearAllSearchData = () => {
    let values: any = {
      color: this.defaultColor
    };
    this.props.form.setData({ filterName: '' });
    this.editFilterInitialData = null;
    this.lastSelectedSearch = null;
    this.setState({
        mode: 'create',
        values
      }, () => this.dispatch('getTaskCount', Position.end, {
        customFieldsActive: this.state.customFieldsActive,
        disabledClearAll: true,
        taskCountData: _.omit(this.state.values, 'filterId'),
        createFilterForm: true,
        fireMainGridFilter: false
      })
    );
  };

  /**
   * Color picker change handler
   * @param event
   */
  colorPickerOnChange = (event: ColorPickerChangeEvent) => {
    this.updateFilterValueChanged();
    // @ts-ignore
    this.setState((oldState: FILTER_FORM_STATE) => {
      return {
        ...oldState,
        values: {
          ...oldState.values,
          color: event.value || undefined
        }
      };
    });
  };

  /**
   * Decodes and converts data to propper format for edit filter.
   */
  decodeEditData = (data: FILTER) => {
    let decodedFilterData = {
      ...data,
      ...data.searchData ? JSON.parse(data.searchData) : {}
    };
    decodedFilterData = _.omit(decodedFilterData, ['searchData', 'isFavorite', 'index', 'ascending', 'folder']);
    this.editFilterInitialData = { ...decodedFilterData };
    return decodedFilterData;
  };

  /**
   * Delete search
   *
   * @param currentItem
   */
  deleteSearch = (currentItem: FILTER) => {
    this.dispatch('deleteSavedSearchAndUpdateDialog', '', {
      filterIndex: currentItem.filterId
    })
  };

  /**
   * Creates array of all custom filed filters.
   */
  getAllCustomFieldsFilters = (filterData: any) => {
    return _.transform(filterData, (agr: any[], value: any, key) => {
      if (!!value.supplierCustomField) {
        agr.push({ ...value })
      }
    }, [])
  };

  /**
   * Return customFieldsActive flag from state object
   */
  getCustomFieldsActive = () => this.state.customFieldsActive

  getFavoritesFilterCount = () => getFavoritesFilterCount(this);

  /**
   * @inheritdoc
   */
  handleParentChange = (name: string, value: any, customFieldChange?: boolean) => {
    this.updateFilterValueChanged();
    if (customFieldChange) {
      this.updateCustomFieldsValue(name, value);
    }
    let submissionCountData = { ...this.state.values };
    let phasesName = this.getPropertyFromFilterData('PHASES_REMOTE_COMBO', 'name');
    submissionCountData = this.parseRemoteComboData(phasesName, submissionCountData);
    const data = _.omit(submissionCountData, ['mode', 'filterId']);
    this.dispatch('getTaskCount', Position.end, {
      customFieldsActive: this.state.customFieldsActive,
      disabledClearAll: this.updateClearButton(),
      taskCountData: data,
      createFilterForm: true,
      fireMainGridFilter: false
    })
  };

  /**
   * Returns data from the dialog
   *
   * @param stringifyFormat
   */
  getData = (customControlValues: any, stringifyFormat = true) => {
    let formData = { ...this.state.values };
    delete formData.filterId;
    for (let prop in formData) {
      const isEmpty = _.isEmpty((formData as any)[prop]) && !_.isDate((formData as any)[prop]) && !_.isNumber((formData as any)[prop]) && (!_.isBoolean((formData as any)[prop]) && (formData as any)[prop]);
      if (isEmpty) {
        delete (formData as any)[prop];
      }
    }
    let jsonSearch: any = {
      index: 0,
      ascending: true,
      folder: 'JOB_PHASE'
    };
    const color = formData.color;
    delete formData.color;
    const filterName = this.props.form.getData().filterName;
    let phasesName = this.getPropertyFromFilterData('PHASES_REMOTE_COMBO', 'name');
    formData = this.parseRemoteComboData(phasesName, formData);
    Object.assign(jsonSearch,
      { ...formData },
      customControlValues ? { customControlValues } : null
    );
    delete jsonSearch.filterName;
    delete jsonSearch.submissionCount;
    let filter = jsonSearch;
    delete filter.mode;
    if (stringifyFormat) {
      filter = {
        color: color,
        filterName,
        isFavorite: true,
        jsonSearch: JSON.stringify(jsonSearch)
      }
    }
    const { mode } = this.state;
    delete filter.jobPhaseCount;
    return {
      filter,
      method: mode === 'edit' ? 'put' : "post",
      url: mode === 'edit' ? `/jobPhase/filters/edit/${this.state.values.filterId}` : '/jobPhase/filters/save'
    };
  };

  /**
   * @inheritdoc
   */
  handleRadioChange = (event: RadioButtonChangeEvent) => {
    let value = event.value;
    this.setState({
      projectRadio: value
    });

    const { values } = this.state;
    if (value === 'allProjects' && values.projectValues?.length) {
      this.dispatch('getSearchBarDataAndUpdateDialog');
    }

    this.onFilterChange('projectValues', null); //clear selected project filter
  };

  pingForFavoritesFilterCount = (responseData: any) => pingForFavoritesFilterCount(responseData, this);

  /**
   * Handle number change and set value to state.
   *
   * @param event
   */
  onNumberChange = (event: any) => {
    if (event.value > -1 && (event.target.value === event.value)) {//when we have min on numeric field and user enters negative values target.values and values are different
      const value = event.target.value;
      const name = event.target.name || '';
      this.onFilterChange(name, value);
    }
  };

  /**
   * @inheritdoc
   */
  onProjectChange = (event:  ComboBoxChangeEvent | MultiSelectChangeEvent) => {
    let value = event.value;
    if (value.length) {
      let projectIds = value.map((item: any) => item.value);
      const params = {
        projectIds
      };
      this.dispatch('clearSearchFormAndGetProjectRelatedData', '', { params });
    } else {
      this.dispatch('getSearchBarDataAndUpdateDialog');
    }

    this.onChangeMulti(event);
  };

  /**
   * On saved searches single click
   *
   * @param currentItem
   * @param event
   */
  onSearchClick = (currentItem: FILTER, event: any) => {
    const skippedClasses = ['tp-filter-list-item-count',
      'tp-filter-list-item-name',
      'tp-filter-list-item-wrap',
      'tp-filter-list-item-settings'];
    if (_.intersection(skippedClasses, event.target.classList).length) {
      this.checkForSearchChanges(currentItem)
    }
  };

  /**
   * On sort end save data in DB
   *
   * @param oldIndex
   * @param newIndex
   */
  onSortEnd = ({ oldIndex, newIndex }: any) => {
    const filters = arrayMove(this.state.filters || [], oldIndex, newIndex);
    this.setState({ filters });
    const params = filters.map(({ filterName }: any, index: number) => {
      return { index, filterName }
    });
    this.dispatch('saveSearchesSort', '', { params })
  };

  /**
   * Handles filter show/hide click
   *
   * @param currentItem
   */
  showHideSearch = (currentItem: FILTER) => {
    this.dispatch('showHideSearch', '', {
      isFavoriteData: {
        isFavorite: !currentItem.isFavorite,
        filterName: currentItem.filterName
      },
      updateDialogSavedSearches: true
    })
  };

  /**
   * Update customControlValues state with new value
   * @param {string} name
   * @param {string | null} value
   */
  updateCustomFieldsValue = (name: string, value: string) => {
    let { values } = this.state;
    let { customControlValues } = values;
    if (customControlValues) {
      let index = _.findIndex(customControlValues, { name });
      if (index === -1) {
        customControlValues.push({ name, value });
      } else {
        customControlValues[index].value = value;
      }
    } else {
      customControlValues = [{ name, value }];
    }
    values.customControlValues = customControlValues;

    this.setState({ values });
  }

  /**
   * Updates dialog footer
   */
  updateClearButton = () => {
    const { values } = this.state;
    const { form } = this.props;
    const { getFields } = form;
    const { filterName } = getFields();
    let disabledClearAll = true;

    Object.keys(_.omit(values, ['mode', 'color'])).forEach((fieldName: any) => {
      if (typeof (values as any)[fieldName] === "string" && (values as any)[fieldName] !== '') {
        disabledClearAll = false;
      }

      if (Array.isArray((values as any)[fieldName]) && (values as any)[fieldName].length > 0) {
        disabledClearAll = false;
      }

      if ([
        'wordCountTo',
        'wordCountFrom',
        'submissionId',
        'fromPhaseDueDate',
        'fromPhaseStartDate',
        'pastDueDateComboBox',
        'toPhaseDueDate',
        'toPhaseStartDate'
      ].includes(fieldName)) {
        if ((values as any)[fieldName] !== null) {
          disabledClearAll = false;
        }
      }
    });

    if (filterName.value !== '') {
      disabledClearAll = false;
    }

    this.dispatch('updateAdvancedSearchFooter', Position.end, {
      disabledClearAll
    })
    return disabledClearAll
  };

  /**
   * Updates dialog footer on edit/clone
   *
   * @param jobPhaseCount
   */
  updateDialogFooter = (jobPhaseCount: number = 0) => {
    this.dispatch('updateSavedSearchesDialogFooter', Position.end, {
      jobPhaseCount: jobPhaseCount
    });
    this.updateClearButton()
  };

  /**
   * Update filter data
   */
  updateFilterData = (newFilterData: FILTER_DATA) => {
    let { filterData } = this.state;
    // @ts-ignore
    filterData = _.omitBy(filterData, (item) => item.custom);

    filterData = {
      ...filterData,
      ...newFilterData
    };

    this.setState({ filterData });
  };

  /**
   * Updates filters with data passed in filters
   *
   * @param filters
   */
  updateFilters = (filters: FILTER[]) => {
    let { filters: oldFilters } = this.state;
    let newFilters: FILTER[] = _.map(filters, (filterItem: FILTER, index: number) => {
      let jobPhaseCount = oldFilters[index].jobPhaseCount;
      return { ...filterItem, jobPhaseCount };
    });

    this.setState({ filters: newFilters });
  };

  /**
   * Updates form with selected saved search
   *
   * @param currentItem
   */
  updateFormWithSelectedSearch = (currentItem: FILTER) => {
    let values = this.decodeEditData(currentItem);
    this.props.form.setData({ filterName: currentItem.filterName || '' });
    let phasesName = this.getPropertyFromFilterData('PHASES_REMOTE_COMBO', 'name');
    if (values[phasesName] && (typeof values[phasesName][0] === 'string')) {
      values[phasesName] = values[phasesName].map((currentPhase: string) => {
        return { name: currentPhase, value: currentPhase }
      })
    }
    this.setState({
      projectRadio: values.projectValues?.length ? 'selectedProject' : 'allProjects',
      values,
      mode: _.isEmpty(currentItem) ? 'create' : 'edit'
    }, () => {
      this.updateDialogFooter(currentItem.jobPhaseCount);
      if (this.state.customFieldsActive) {
        this.dispatch('loadCustomFieldsValues', Position.end, { values });
      }
    });
  };

  renderBody() {
    const { customAttributesValues, displayFilterName, form } = this.props;
    const { getFields, handleChange } = form;
    const { filterName } = getFields();
    const { customFieldsActive, projectRadio, values, filterData, filters, mode } = this.state;
    const customFieldsFilters = this.getAllCustomFieldsFilters(filterData);

    return (filterData &&
      <div className={'tp-filter-dialog'}>
        <HBox>
          <VBox className={'tp-saved-filters-pane'}>
            <Container className={'tp-create-new-filter-btn-wrapper'}>
              <PuzzleButton
                fillMode="flat"
                iconClass={"tp-icon tp-add"}
                className={`tp-create-new-filter-btn ${mode !== 'edit' ? 'tp-marked-filter' : ''}`}
                onClick={() => this.checkForSearchChanges({})}
              >
                {this.translate('createNewSearch')}
              </PuzzleButton>
            </Container>
            <VBox>
              <SortableList
                ariaLabel={this.translate('SRL-advancedSearchFilterList')}
                axis='y'
                deleteSearch={this.deleteSearch}
                distance={5}
                editedFilterId={mode === 'edit' ? this.state.values.filterId : null}
                emptyFilterListLabel={this.translate('emptyFilerList')}
                items={filters}
                helperClass={'tp-drag-filter'}
                onSearchClick={this.onSearchClick}
                onSortEnd={this.onSortEnd}
                showHideSearch={this.showHideSearch}
              />
            </VBox>
          </VBox>
          <div className={'tp-vertical-line'}></div>
          <div className={'tp-filter-form tp-filter-form-pane'}>
            {
              displayFilterName &&
              <FieldWrap
                withValidation
                className={`tp-colorpicker-field-wrap`}
              >
                <PuzzleInput
                  asField
                  fieldMandatory
                  label={this.translate('labelName')}
                  id="filterName"
                  name="filterName"
                  onChange={(e: any) => {
                    this.updateFilterValueChanged();
                    handleChange(e);
                  }}
                  errorTxt={this.translateErrorMessage(filterName)}
                  withValidation
                  {...filterName}
                />
                <Field
                  className={values.color === 'no_color' ? 'tp-transparent-indicator' : ''}>
                  <PuzzleColorPicker
                    ariaLabelledBy={'colorpicker'}
                    value={values.color}
                    dropdownSettings={this.paletteSettings}
                    id={'colorpicker'}
                    label={this.translate('filterColor')}
                    look={'circle'}
                    onChange={this.colorPickerOnChange}
                    palette={this.colorPaletteForPicker}
                    valueFieldRgb={false}
                  />
                </Field>
              </FieldWrap>
            }
            <PuzzlePanel
              title={this.translate('taskDetails')}
              titleLine
            >
              <fieldset className={'tp-filter-form-select-project tp-no-border'}>
                <legend className="sr-only">{this.translate('projects')}</legend>
                <FieldWrap
                  className={'k-form-field-wrap tp-filter-form-select-project-wrap k-hbox k-align-items-center'}>
                  <Field
                    className={'tp-filter-form-project-radio-wrap'}
                  >
                    <PuzzleRadio
                      checked={projectRadio === 'allProjects'}
                      id={'allProjects'}
                      label={this.translate('allProjects')}
                      name='projects'
                      onChange={this.handleRadioChange}
                      value='allProjects'
                    />
                    <PuzzleRadio
                      checked={projectRadio === 'selectedProject'}
                      id={'selectedProject'}
                      label={this.translate('selectedProject')}
                      name='projects'
                      onChange={this.handleRadioChange}
                      value='selectedProject'
                    />
                  </Field>
                  <Field>
                    <PuzzleTagField
                      label={this.translate('selectedProject')}
                      id={"projectValues"}
                      name="projectValues"
                      autoClose={false}
                      popupSettings={{ className: this.props.zIndexCls }}
                      data={this.getPrefilteredData('PROJECT_VALUES')}
                      disabled={projectRadio === 'allProjects'}
                      onChange={this.onProjectChange}
                      textField={'name'}
                      value={values.projectValues || []}
                      valueField={'value'}
                    />
                  </Field>
                </FieldWrap>
              </fieldset>
              <FieldWrap>
                <Field>
                  <PuzzleInput
                    label={this.translate('taskName')}
                    id={"submissionNameInputText"}
                    name="submissionNameInputText"
                    onChange={this.onInputChange}
                    value={values.submissionNameInputText || ''}
                  />
                </Field>
              </FieldWrap>
              <FieldWrap layout={[6, 6]}>
                <Field className={'tp-id-field'}>
                  <PuzzleNumericField
                    id={"submissionId"}
                    label={this.translate('idText')}
                    format={''}
                    min={0}
                    name="submissionId"
                    onChange={this.onNumberChange}
                    spinners={false}
                    value={values.submissionId || null}
                  />
                </Field>
                <FieldWrap
                  className={'tp-word-count-wrap'}
                >
                  <span className="k-label">{this.translate('wordCount')}</span>
                  <Field>
                    <PuzzleNumericField
                      id={"wordCountFrom"}
                      label={this.translate('from')}
                      format={''}
                      max={values.wordCountTo && (values.wordCountTo - 1)}
                      name="wordCountFrom"
                      onChange={this.onNumberChange}
                      spinners={false}
                      value={values.wordCountFrom || null}
                    />
                  </Field>
                  <Field>
                    <PuzzleNumericField
                      id={"wordCountTo"}
                      label={this.translate('to')}
                      format={''}
                      min={values.wordCountFrom && (values.wordCountFrom + 1)}
                      name="wordCountTo"
                      onChange={this.onNumberChange}
                      spinners={false}
                      value={values.wordCountTo || null}
                    />
                  </Field>

                </FieldWrap>
              </FieldWrap>
              <FieldWrap layout={[6, 6]}>
                {filterData.SOURCE_LANGUAGE_VALUES &&
                  <Field>
                    <PuzzleTagField
                      label={this.translate('sourceLanguage')}
                      name="sourceLanguageValues"
                      id={"sourceLanguageValues"}
                      autoClose={false}
                      popupSettings={{ className: this.props.zIndexCls }}
                      data={this.getPrefilteredData('SOURCE_LANGUAGE_VALUES')}
                      onChange={this.onChangeMulti}
                      textField={'name'}
                      value={values.sourceLanguageValues || []}
                      valueField={'value'}
                    />
                  </Field>}
                {filterData.TARGET_LANGUAGE_VALUES &&
                  <Field>
                    <PuzzleTagField
                      label={this.translate('targetLanguage')}
                      id={"targetLanguageValues"}
                      name="targetLanguageValues"
                      autoClose={false}
                      popupSettings={{ className: this.props.zIndexCls }}
                      data={this.getPrefilteredData('TARGET_LANGUAGE_VALUES')}
                      onChange={this.onChangeMulti}
                      textField={'name'}
                      value={values.targetLanguageValues || []}
                      valueField={'value'}
                    />
                  </Field>}
              </FieldWrap>
              <FieldWrap>
                <Field>
                  <PuzzleInput
                    id={"fileNameInputText"}
                    label={this.translate('fileName')}
                    name="fileNameInputText"
                    onChange={this.onInputChange}
                    value={values.fileNameInputText || ''}
                  />
                </Field>
              </FieldWrap>
              <FieldWrap layout={[6, 6]}>
                {filterData.PRIORITY_LIST &&
                  <Field>
                    <PuzzleTagField
                      label={this.translate('priorityLevel')}
                      name="priority"
                      id={"priority"}
                      autoClose={false}
                      popupSettings={{ className: this.props.zIndexCls }}
                      data={this.getPrefilteredData('PRIORITY_LIST')}
                      onChange={this.onChangeMulti}
                      textField={'name'}
                      value={values.priority || []}
                      valueField={'value'}
                    />
                  </Field>}
              </FieldWrap>
            </PuzzlePanel>
            <PuzzlePanel
              title={this.translate('status')}
              titleLine
            >
              <FieldWrap layout={[6, 6]}>
                {filterData.TASK_STATUS_VALUES &&
                  <Field>
                    <PuzzleTagField
                      autoClose={false}
                      data={this.taskStatusData}
                      label={this.translate('taskStatus')}
                      id={"taskStatusValues"}
                      name="taskStatusValues"
                      onChange={this.onChangeMulti}
                      popupSettings={{ className: this.props.zIndexCls }}
                      textField={'name'}
                      translate={this.translate.bind(this)}
                      value={values.taskStatusValues || []}
                      valueField={'value'}
                      translateItems={false}
                    />
                  </Field>}
                <Field>
                  <RemoteTagField
                    label={this.translate('phase')}
                    name={this.getPropertyFromFilterData('PHASES_REMOTE_COMBO', 'name')}
                    id={this.getPropertyFromFilterData('PHASES_REMOTE_COMBO', 'name')}
                    onChange={this.onChangeMulti}
                    textField={'name'}
                    value={(values as any)[this.getPropertyFromFilterData('PHASES_REMOTE_COMBO', 'name')] || []}
                    valueField={'value'}
                    valuesProvider={this.getPropertyFromFilterData('PHASES_REMOTE_COMBO', 'valuesProvider')}
                  />
                </Field>
              </FieldWrap>
              <FieldWrap layout={[6, 6]}>
                {filterData.VENDOR_BUDGET_STATUSES &&
                  <Field>
                    <PuzzleTagField
                      autoClose={false}
                      data={this.getPrefilteredData('VENDOR_BUDGET_STATUSES', true)}
                      id={"vendorBudgetStatuses"}
                      label={this.translate('offerStatus')}
                      name="vendorBudgetStatuses"
                      onChange={this.onChangeMulti}
                      popupSettings={{ className: this.props.zIndexCls }}
                      textField={'name'}
                      translate={this.translate.bind(this)}
                      translateItems={false}
                      value={values.vendorBudgetStatuses || []}
                      valueField={'value'}
                    />
                  </Field>}
              </FieldWrap>
            </PuzzlePanel>


            <PuzzlePanel
              title={this.translate('timeline')}
              titleLine
            >
              <FieldWrap className={'k-date-field-wrap'}>
                <span className="k-label">{this.translate('phaseStartDate')}</span>
                <Field>
                  <LocalizedDatePicker
                    name="fromPhaseStartDate"
                    id={"fromPhaseStartDate"}
                    onChange={this.onDateChange}
                    popupSettings={{ popupClass: this.props.zIndexCls }}
                    value={(values.fromPhaseStartDate && new Date(values.fromPhaseStartDate)) || null}
                    valid
                    className={'tp-without-error-text'}
                    max={(values.toPhaseStartDate && new Date(values.toPhaseStartDate)) || undefined}
                    format={this.translateByNamespace('date.datePickerFormat')}
                    formatPlaceholder={getFormattedDate(this.translate)}
                    label={this.translate('from')}
                    ariaLabelLocale={this.translate('SRL-dateCreatedFrom')}
                  />
                </Field>
                <Field>
                  <LocalizedDatePicker
                    id={"toPhaseStartDate"}
                    name="toPhaseStartDate"
                    value={(values.toPhaseStartDate && new Date(values.toPhaseStartDate)) || null}
                    min={(values.fromPhaseStartDate && new Date(values.fromPhaseStartDate)) || undefined}
                    onChange={this.onDateChange}
                    valid
                    className={'tp-without-error-text'}
                    popupSettings={{ popupClass: this.props.zIndexCls }}
                    format={this.translateByNamespace('date.datePickerFormat')}
                    formatPlaceholder={getFormattedDate(this.translate)}
                    label={this.translate('to')}
                    ariaLabelLocale={this.translate('SRL-dateCreatedTo')}
                  />
                </Field>
              </FieldWrap>
              <FieldWrap className={'k-date-field-wrap'}>
                <span className="k-label">{this.translate('phaseDueDate')}</span>
                <Field>
                  <LocalizedDatePicker
                    name="fromPhaseDueDate"
                    id={"fromPhaseDueDate"}
                    className={'tp-without-error-text'}
                    value={(values.fromPhaseDueDate && new Date(values.fromPhaseDueDate)) || null}
                    max={(values.toPhaseDueDate && new Date(values.toPhaseDueDate)) || undefined}
                    onChange={this.onDateChange}
                    valid
                    popupSettings={{ popupClass: this.props.zIndexCls }}
                    format={this.translateByNamespace('date.datePickerFormat')}
                    formatPlaceholder={getFormattedDate(this.translate)}
                    label={this.translate('from')}
                    ariaLabelLocale={this.translate('SRL-dueDateFrom')}
                  />
                </Field>
                <Field>
                  <LocalizedDatePicker
                    name="toPhaseDueDate"
                    id={"toPhaseDueDate"}
                    className={'tp-without-error-text'}
                    value={(values.toPhaseDueDate && new Date(values.toPhaseDueDate)) || null}
                    valid
                    min={(values.fromPhaseDueDate && new Date(values.fromPhaseDueDate)) || undefined}
                    onChange={this.onDateChange}
                    popupSettings={{ popupClass: this.props.zIndexCls }}
                    format={this.translateByNamespace('date.datePickerFormat')}
                    formatPlaceholder={getFormattedDate(this.translate)}
                    label={this.translate('to')}
                    ariaLabelLocale={this.translate('SRL-dueDateTo')}
                  />
                </Field>
              </FieldWrap>
              <FieldWrap className={'tp-past-due-field k-date-field-wrap'}>
                <span className="k-label">{this.translate('pastDueLabel')}</span>
                <Field className={'tp-past-due-date'}>
                  <FilterCombo
                    label={this.translate('pastDue')}
                    id={"pastDueDateComboBox"}
                    name="pastDueDateComboBox"
                    popupSettings={{ className: this.props.zIndexCls }}
                    data={this.getPrefilteredData('PAST_DUE_DATE_LIST')}
                    textField={'name'}
                    onChange={this.onChangeMulti}
                    value={values.pastDueDateComboBox || null}
                    valueField={'value'}
                    valueAsSimpleType={true}
                  />
                </Field>
              </FieldWrap>
            </PuzzlePanel>
            {customFieldsActive ? <PuzzlePanel
              title={this.translate('customFields')}
              titleLine
              className={'tp-custom-fields-panel'}
            >
              <CustomFieldsFilter
                parentChange={this.handleParentChange}
                isSupplier={true}
                customFieldsFilters={customFieldsFilters}
                customAttributesValues={customAttributesValues}
              />
            </PuzzlePanel> : null}
          </div>
        </HBox>
      </div>
    )
  }
}

export default withFormControl(config)((AdvancedSearchDialog as any));
